<template>
  <div :id="`${source}_notes_modal`">
    <CustomModal
      :visible="visible"
      @update:visible="$emit('update:visible', $event)"
      title="Add Note"
      width="40vw"
    >
      <div class="flex flex-col gap-2">
        <div
          class="flex flex-col gap-1 secondary_color font-normal text-sm mail_reply_container"
        >
          <QuillEditor
            placeholder="Enter note details"
            v-model="notes"
            class="w-full"
            :height="250"
            auto-focus
            :attachments="attachments"
            @remove-attachment="removeAttachment"
          />
        </div>
        <div class="flex justify-end">
          <AttachmentButton
            source="add_notes_modal"
            @file-selected="handleFileChange"
            visible
          />
        </div>
      </div>

      <template #footer>
        <a-button
          class="primary_medium"
          @click="handleConfirm"
          :disabled="isAddButtonDisabled"
          :loading="loading"
        >
          Add
        </a-button>
      </template>
    </CustomModal>
  </div>
</template>

<script setup>
import { ref, defineProps, defineEmits, watch, computed, onMounted } from "vue";
import CustomModal from "@/src/components/CustomModal.vue";
import QuillEditor from "@/src/components/quill_editor/QuillEditor.vue";
import AttachmentButton from "@/src/components/attachment_components/AttachmentButton.vue";
import { useFileUpload } from "@/src/composables/useFileUpload.js";
import { hasNoteContent, hasNoteAttachments } from "@/src/constants/noteConstants.js";

const props = defineProps({
  source: { type: String, required: true },
  visible: { type: Boolean, required: true },
  loading: { type: Boolean, required: true },
});

const emit = defineEmits(["update:visible", "confirm"]);

const {
  attachments,
  uploadFiles,
  removeAttachment,
  resetAttachments,
  isUploading,
} = useFileUpload();

const domParser = ref(null);
const notes = ref("");
const isAddButtonDisabled = ref(false);

const handleConfirm = () => {
  emit("confirm", { notes: notes.value, attachments: attachments.value });
};

const handleFileChange = async (files = []) => {
  if (files?.length === 0) return;
  await uploadFiles(files);
};

const validateNotes = () => {
  try {
    const hasContent = hasNoteContent(domParser.value, notes.value);
    const hasAttachments = hasNoteAttachments(attachments.value);
    isAddButtonDisabled.value =
      (!hasContent && !hasAttachments) || isUploading.value;
  } catch (error) {
    console.error("Error validating notes:", error);
    isAddButtonDisabled.value = true;
  }
};

watch(
  () => props.visible,
  (newVal) => {
    if (!newVal) {
      notes.value = "";
      resetAttachments();
    }
  },
  {
    immediate: true,
  },
);

watch(() => notes.value, validateNotes, {
  immediate: true,
});
watch(attachments, validateNotes, { deep: true });

onMounted(() => {
  domParser.value = new DOMParser();
});
</script>
